"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-primary/40 rounded-md bg-card">
        <p className="font-serif text-2xl text-foreground mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
          Submit another request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/60">
              Full Name
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder="Alexander Thorne" className="bg-transparent border-0 border-b border-border px-0 h-11 text-base font-default text-foreground placeholder:text-foreground/30 focus-visible:ring-0 focus-visible:border-primary" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem className="gap-2">
              <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/60">
                Email Address
              </FormLabel>
              <FormControl>
                <Input {...field} type="email" placeholder="studio@thorne.com" className="bg-transparent border-0 border-b border-border px-0 h-11 text-base font-default text-foreground placeholder:text-foreground/30 focus-visible:ring-0 focus-visible:border-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="gap-2">
              <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/60">
                Contact Number
              </FormLabel>
              <FormControl>
                <Input {...field} type="tel" placeholder="+44 20 7123 4567" className="bg-transparent border-0 border-b border-border px-0 h-11 text-base font-default text-foreground placeholder:text-foreground/30 focus-visible:ring-0 focus-visible:border-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="event_type" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/60">
              Project Category
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-transparent border-0 border-b border-border px-0 h-11 w-full text-base font-default text-foreground data-[placeholder]:text-foreground/30 focus-visible:ring-0 focus-visible:border-primary [&_svg]:text-primary">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="Bespoke Tailoring">Bespoke Tailoring</SelectItem>
                <SelectItem value="Editorial Styling">Editorial Styling</SelectItem>
                <SelectItem value="Textile Sourcing">Textile Sourcing</SelectItem>
                <SelectItem value="Pattern Development">Pattern Development</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="event_date" render={({
        field
      }) => <FormItem className="gap-2">
              <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/60">
                Timeline
              </FormLabel>
              <FormControl>
                <Input {...field} type="date" className="bg-transparent border-0 border-b border-border px-0 h-11 text-base font-default text-foreground placeholder:text-foreground/30 focus-visible:ring-0 focus-visible:border-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="guests" render={({
        field
      }) => <FormItem className="gap-2">
              <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/60">
                Budget Range (USD)
              </FormLabel>
              <FormControl>
                <Input {...field} type="number" placeholder="e.g., 5000" className="bg-transparent border-0 border-b border-border px-0 h-11 text-base font-default text-foreground placeholder:text-foreground/30 focus-visible:ring-0 focus-visible:border-primary" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="gap-2">
            <FormLabel className="font-mono text-[11px] uppercase tracking-[0.2em] text-foreground/60">
              Project Brief
            </FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Describe your vision, technical requirements, or specific fabric preferences..." className="bg-transparent border-0 border-b border-border px-0 py-2 text-base font-default text-foreground placeholder:text-foreground/30 focus-visible:ring-0 focus-visible:border-primary resize-none transition-all focus:min-h-32" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="updates" render={({
      field
    }) => <FormItem className="flex flex-row items-start gap-3 space-y-0">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-1 border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground" />
            </FormControl>
            <FormLabel className="text-sm font-default text-foreground/80 leading-relaxed cursor-pointer">
              Subscribe to Studio Thorne technical updates
            </FormLabel>
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 rounded-md">
          <p className="text-destructive text-sm font-mono">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} variant="outline" className="group w-full h-14 bg-transparent border border-primary text-primary hover:bg-primary hover:text-primary-foreground font-mono text-xs uppercase tracking-[0.25em] rounded-md transition-all">
        <span>{isSubmitting ? "Processing..." : "Submit Brief"}</span>
        <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
      </Button>
    </Form>;
}
export default function CollaborateWithThorne() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3479" id="collaborate-with-thorne" className="bg-background text-foreground py-24 md:py-[180px] px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-16 lg:gap-24">
          <div ref={leftRef} className={`self-start transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-10">
              Studio Collaboration
            </p>
            <h2 className="font-default text-4xl md:text-5xl lg:text-[64px] leading-[1.05] text-foreground mb-8">
              Architecting
              <br />
              <em className="italic font-light text-primary">your silhouette.</em>
            </h2>
            <p className="font-default text-[17px] leading-relaxed text-foreground/80 mb-16 max-w-md">
              Our atelier accepts a limited number of private commissions per season. Every inquiry is reviewed for technical feasibility and alignment with our design ethos.
            </p>

            <div className="border-t border-border/40 pt-10 space-y-4">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-foreground/50">
                Direct Atelier Access
              </p>
              <div className="space-y-2">
                <Link to="tel:+442071234567" className="block font-default text-lg text-primary hover:text-primary/80 transition-colors">
                  +44 20 7123 4567
                </Link>
                <Link to="mailto:atelier@studiothorne.com" className="block font-mono text-sm text-primary hover:text-primary/80 transition-colors">
                  atelier@studiothorne.com
                </Link>
              </div>
            </div>
          </div>

          <div ref={rightRef} className={`transition-all duration-1000 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-foreground/50 mb-4">
              * All project briefs are handled with strict confidentiality.
            </p>

            <div className="bg-card border border-primary/30 rounded-md p-8 md:p-16 shadow-lg">
              <FormProvider formKey="collaborate_form_inquiries" formId="collaborate_form_inquiries" sectionName="collaborate-with-thorne" defaultValues={{
              name: "",
              email: "",
              phone: "",
              event_date: "",
              guests: "",
              event_type: "",
              message: "",
              updates: false
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              event_date: {
                required: true
              },
              guests: {
                required: true,
                min: 1
              },
              event_type: {
                required: true
              }
            }} submitText="Submit Brief" successMessage="Brief received. Our lead designer will review your requirements and contact you within 48 hours." errorMessage="Transmission failed. Please verify your details or contact the atelier directly.">
                <InquiryForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}